/**
 * =============================================================================
 * SPEQ55 RFP Pro - Contact Page
 * =============================================================================
 *
 * Public contact page on the marketing website. This page allows potential
 * customers and visitors to send inquiries to the sales team without requiring
 * authentication.
 *
 * FEATURES:
 * ---------
 * - Contact form with name, email, company (optional), and message fields
 * - Form validation with required field indicators
 * - Loading state during submission
 * - Success/error feedback messages
 * - Contact information display (email, phone)
 *
 * DATA FLOW:
 * ----------
 * 1. Visitor fills out the contact form
 * 2. Form data is sent to POST /api/contact (public endpoint, rate-limited)
 * 3. Backend sends email notification to sales@speq55.com
 * 4. Visitor sees success confirmation
 *
 * SECURITY:
 * ---------
 * - No authentication required (public page)
 * - Backend rate-limits to 5 submissions per IP per 15 minutes
 * - Input validation on both frontend and backend
 *
 * @module pages/public/Contact
 * @author SPEQ55
 * @see {@link backend/src/routes/contact.js} for API endpoint
 */

import { useState } from 'react';
import axios from 'axios';

// =============================================================================
// CONTACT INFORMATION
// =============================================================================

/**
 * Company contact information displayed alongside the form.
 * Each item includes a label, display value, optional href for linking,
 * and an SVG icon component.
 *
 * @constant {Array<Object>}
 */
const contactInfo = [
  {
    label: 'Email',
    value: 'info@SPEQ55.com',
    href: 'mailto:info@SPEQ55.com',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    label: 'Phone',
    value: '+1 (555) 555-5555',
    href: 'tel:+15555555555',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
        />
      </svg>
    ),
  },
];

// =============================================================================
// CONTACT PAGE COMPONENT
// =============================================================================

/**
 * Contact page component for the public marketing website.
 *
 * Renders a two-column layout on desktop:
 * - Left column: Contact form
 * - Right column: Contact information (email, phone)
 *
 * On mobile, columns stack vertically.
 *
 * @returns {JSX.Element} Contact page with form and contact info
 */
function Contact() {
  // ---------------------------------------------------------------------------
  // Form State
  // ---------------------------------------------------------------------------
  // Stores the values for all form fields

  const [formData, setFormData] = useState({
    name: '', // Visitor's name (required)
    email: '', // Visitor's email address (required)
    company: '', // Visitor's company name (optional)
    message: '', // The inquiry message (required)
  });

  // ---------------------------------------------------------------------------
  // Submission State
  // ---------------------------------------------------------------------------
  // isSubmitting: True while API request is in progress (shows loading spinner)
  // submitStatus: 'success' | 'error' | null - determines which message to show

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // ===========================================================================
  // EVENT HANDLERS
  // ===========================================================================

  /**
   * Handle form input changes for text fields.
   * Updates the corresponding field in formData state using the input's name attribute.
   *
   * @param {React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>} e - Change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Handle form submission.
   *
   * Workflow:
   * 1. Prevent default form submission behavior
   * 2. Set loading state to show spinner
   * 3. POST form data to /api/contact endpoint
   * 4. On success: show success message, reset form fields
   * 5. On error: show error message
   * 6. Always reset loading state when complete
   *
   * Note: Uses axios directly instead of api client because this is a public
   * page that doesn't require authentication.
   *
   * @param {React.FormEvent<HTMLFormElement>} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Get API URL from environment variable or use default proxy path
      const apiUrl = import.meta.env.VITE_API_URL || '/api';

      // Send contact form data to the backend
      // Backend will email this to sales@speq55.com
      await axios.post(`${apiUrl}/contact`, formData);

      // Submission successful
      setSubmitStatus('success');

      // Reset form to empty state
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch {
      // Show error state to user
      setSubmitStatus('error');
    } finally {
      // Always reset loading state, whether success or error
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white">Contact Us</h1>
          <p className="mt-6 text-xl text-primary-100 max-w-3xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon
            as possible.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-accent-900 mb-6">Send us a Message</h2>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-secondary-50 border border-secondary-200 rounded-lg">
                  <div className="flex items-center">
                    <svg
                      className="h-5 w-5 text-secondary-500 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <p className="text-secondary-700">
                      Thank you! Your message has been sent. We'll get back to you soon.
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg
                      className="h-5 w-5 text-red-500 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-red-700">Something went wrong. Please try again later.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-accent-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-4 py-2 border border-accent-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-accent-100 disabled:cursor-not-allowed"
                    placeholder="Your name"
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-accent-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-4 py-2 border border-accent-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-accent-100 disabled:cursor-not-allowed"
                    placeholder="you@company.com"
                  />
                </div>

                {/* Company Field */}
                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-accent-700 mb-1"
                  >
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-4 py-2 border border-accent-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-accent-100 disabled:cursor-not-allowed"
                    placeholder="Your company name"
                  />
                </div>

                {/* Message Field */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-accent-700 mb-1"
                  >
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-4 py-2 border border-accent-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-accent-100 disabled:cursor-not-allowed resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-accent-900 mb-6">Contact Information</h2>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                      {item.icon}
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-accent-500">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="text-accent-900 hover:text-primary-600 transition-colors duration-200"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-accent-900 whitespace-pre-line">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
